import React, { useState, useRef, useEffect } from "react";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { RightOutlined } from "@ant-design/icons";
import StepsProgress from "organisms/StepsProgress";
import BasicAndKPI from "./BasicAndKPI";
import styled from "styled-components";
import AppraiseeForm from "./AppraiseeForm";
import AppraiserForm from "./AppraiserForm";
import ScoreAndIncrement from "./ScoreAndIncrement";
import { Tabs, message } from "antd";
import COLORS from "common/Colors";
import { CALL_API } from "common/API";
import dayjs from "dayjs";
import { STATUS_CODE } from "common/Constants";
import SuccessDeclinePopup from "reusableComponent/Popup/SuccessDeclinePopup";

const Wrap = styled.div`
  position: relative;
  height: calc(100vh - 150px);
  .ant-input,
  .ant-btn {
    border-radius: 5px !important;
  }
  .ant-select .ant-select-selector {
    border-radius: 5px !important;
  }
  .ant-tabs-nav {
    border-radius: 5px;
    font-weight: 500;
    color: ${COLORS.TEXT.PRIMARY};
  }
  .ant-tabs-tab {
    font-size: 16px;
    &:hover {
      color: ${COLORS.TEXT.PRIMARY};
    }
  }
  .ant-tabs-ink-bar {
    background: ${COLORS.TAB};
    font-weight: bold;
    position: absolute;
    bottom: -1px !important;
  }
  .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
    color: ${COLORS.TAB};
    font-weight: 500;
    font-size: 16px;
  }
  .ant-tabs-nav::before {
    border: none !important;
  }
  .ant-tabs-nav-list {
    border-bottom: 2px solid #80808033;
  }
  .ant-table-cell {
    padding: 16px;
    white-space: normal;
  }

  .wrap-column {
    white-space: normal !important;
    word-break: break-word;
    min-width: 200px;
  }
  @media (min-width: 320px) and (max-width: 420px) {
    .ant-tabs-tab {
      margin: 0 !important;
    }
    .ant-tabs-nav .ant-tabs-nav-list {
      gap: 1rem;
    }
  }
`;

const footerStyle = {
  position: "absolute",
  bottom: -40,
  left: 0,
  right: 0,
  backgroundColor: "#fff",
  padding: "10px",
  display: "flex",
  justifyContent: "end",
  boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
  margin: "0px -20px",
};

const AddEditTemplate = ({
  setIsAddEditTemplate,
  isAddEditTemplate,
  status,
  setStatus,
  dataList,
  fetchData,
}) => {
  const history = useHistory();
  const topRef = useRef(null);
  const [step, setStep] = useState(0);
  const [tabId, setTabId] = useState("1");
  const [loading, setLoading] = useState(false);
  const [draftLoading, setDraftLoading] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [updateStatus, setUpdateStatus] = useState(false);

  const [introductoryDetail, setIntroductoryDetail] = useState(
    dataList?.introductorysection || []
  );
  const [mainSectionDetail, setMainSectionDetail] = useState(
    dataList?.mainquestionnairesection || []
  );
  const [remarkSectionDetail, setRemarkSectionDetail] = useState(
    dataList?.remarkssection || []
  );
  const [tableData, setTableData] = useState(
    dataList?.kpirole || [
      {
        id: 1,
        kpicategory: "",
        kpidescription: "",
        expectedoutcomes: "",
        weightage: "",
      },
    ]
  );
  const [data, setData] = useState({
    templatename: dataList?.templatename || "",
    availabledepartments: dataList?.availabledepartments || [],
    templatestatus: dataList?.templatestatus || "",
  });
  const [salaryData, setSalaryData] = useState(
    dataList?.salaryincpercentage || [
      { id: 1, salaryfrom: 0, salaryto: "", label: "", description: "" },
    ]
  );
  const [scoreData, setScoreData] = useState(
    dataList?.scoreclassification || [
      {
        key: 0,
        experience: "",
        score1: "",
        score2: "",
        score3: "",
        score4: "",
        score5: "",
      },
    ]
  );
  const [appraisarIntroductoryDetail, setAppraisarIntroductoryDetail] =
    useState(dataList?.appraiserintroductorysection || []);
  const [appraisarMainSectionDetail, setAppraisarMainSectionDetail] = useState(
    dataList?.appraisermainquestionnairesection || []
  );
  const [appraisarRemarkSectionDetail, setAppraisarRemarkSectionDetail] =
    useState(dataList?.appraiserremarkssection || []);
  const [scoreClassificationData, setScoreClassificationData] = useState([]);

  const isStep2Initialized = useRef(false);

  const saveAppraiseDetails = () => {
    if (!isStep2Initialized.current && status !== "draft") {
      setAppraisarIntroductoryDetail(
        JSON.parse(JSON.stringify(introductoryDetail))
      );
      setAppraisarMainSectionDetail(
        JSON.parse(JSON.stringify(mainSectionDetail))
      );
      setAppraisarRemarkSectionDetail(
        JSON.parse(JSON.stringify(remarkSectionDetail))
      );
      isStep2Initialized.current = true;
    }
  };

  // ✅ Master validation — checks all sections before final submit
  const validateAll = () => {
    const errors = [];

    // 1. Validate Basic — template name, department, status
    if (!data?.templatename?.trim()) {
      errors.push("Template name is required in Basics & KPI.");
    }
    if (!data?.availabledepartments?.length) {
      errors.push("Please select at least one department in Basics & KPI.");
    }
    if (!data?.templatestatus) {
      errors.push("Template status is required in Basics & KPI.");
    }

    // 2. Validate KPI table — at least one row, all fields filled, weightage = 100
    if (!tableData?.length) {
      errors.push("Please add at least one KPI row in Basics & KPI.");
    } else {
      const incompleteKpi = tableData.some(
        (row) =>
          !row.kpicategory ||
          !row.kpidescription ||
          !row.expectedoutcomes ||
          !row.weightage
      );
      if (incompleteKpi) {
        errors.push("Please fill all fields in the KPI table in Basics & KPI.");
      }
      const kpiTotal = tableData.reduce(
        (sum, item) => sum + (parseFloat(item.weightage) || 0),
        0
      );
      if (kpiTotal !== 100) {
        errors.push(
          "KPI total weightage must be exactly 100% in Basics & KPI."
        );
      }
    }

    // 3. Validate Appraisee main section — at least one filled question
    const appraiseeHasQuestion =
      Array.isArray(mainSectionDetail) &&
      mainSectionDetail?.every((section) => section?.question !== "");
    if (!appraiseeHasQuestion) {
      errors.push(
        "Main Questionnaire Section in Appraisee's Form requires all the questions to be filled."
      );
    }

    // 4. Validate Appraiser main section — at least one filled question
    const appraiserHasQuestion =
      Array.isArray(appraisarMainSectionDetail) &&
      appraisarMainSectionDetail?.every((section) => section?.question !== "");
    if (!appraiserHasQuestion) {
      errors.push(
        "Main Questionnaire Section in Appraiser's Form requires all the questions to be filled."
      );
    }

    // 5. Validate appraiser questions not more than appraisee
    if (
      Array.isArray(appraisarMainSectionDetail) &&
      Array.isArray(mainSectionDetail) &&
      appraisarMainSectionDetail.length > mainSectionDetail.length
    ) {
      errors.push(
        "Appraiser's form cannot have more questions than Appraisee's form."
      );
    }

    if (errors.length > 0) {
      errors.forEach((err) => message.error(err));
      return false;
    }

    return true;
  };

  const items = [
    { key: "1", label: "Basics & KPI" },
    { key: "2", label: "Appraisee's Form" },
    { key: "3", label: "Appraiser's Form" },
    { key: "4", label: "Score & Increment" },
  ];

  const stepdata = [
    { id: 1, title: "Basics & KPI" },
    { id: 2, title: "Appraisee's Form" },
    { id: 3, title: "Appraiser's Form" },
    { id: 4, title: "Score & Increment" },
  ];

  const onChange = (key) => {
    setTabId(key);
  };

  const onSubmitClick = async (Status) => {
    if (Status === "draft") {
      setUpdateStatus("draft");
      setDraftLoading(true);
    } else {
      setLoading(true);
    }

    const payload = {
      templatename: data?.templatename || "",
      availabledepartments: data?.availabledepartments || [],
      templatestatus: data?.templatestatus || "",
      kpirole: tableData || [],
      mainquestionnairesection: mainSectionDetail || [],
      introductorysection: introductoryDetail || [],
      remarkssection: remarkSectionDetail || [],
      appraisermainquestionnairesection: appraisarMainSectionDetail || [],
      appraiserintroductorysection: appraisarIntroductoryDetail || [],
      appraiserremarkssection: appraisarRemarkSectionDetail || [],
      scoreclassification: scoreData || [],
      salaryincpercentage: salaryData || [],
      updatedon: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      statusid: Status === "draft" ? "0" : "1",
    };

    const { code } = await CALL_API(
      status === "edit" || dataList?.templateid
        ? `performance/performancetemplate/${dataList?.templateid}`
        : `performance/performancetemplate`,
      status === "edit" || dataList?.templateid ? "patch" : "post",
      payload
    );

    if (code === STATUS_CODE.SUCCESS) {
      setPopupOpen(true);
      setTimeout(() => {
        setPopupOpen(false);
        setUpdateStatus("");
        setStatus("");
        setIsAddEditTemplate(false);
      }, 2000);
      fetchData();

      setDraftLoading(false);
      setLoading(false);
    }
  };

  const scrollToTop = () => {
    topRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Sync introductory section
  useEffect(() => {
    if (!isStep2Initialized.current) return;
    setAppraisarIntroductoryDetail((prev) => {
      const updated = [...introductoryDetail];
      return updated.map((item, i) => ({
        ...item,
        // preserve appraiser's own answer/changes if any, only sync structure
        ...(prev[i]
          ? { formType: prev[i].formType, options: prev[i].options }
          : {}),
        question: item.question,
      }));
    });
  }, [introductoryDetail]);

  // Sync main section
  useEffect(() => {
    if (!isStep2Initialized.current) return;
    setAppraisarMainSectionDetail((prev) => {
      return introductoryDetail.map ? introductoryDetail : prev; // guard
    });
  }, [introductoryDetail]);

  return (
    <Wrap>
      <div ref={topRef} />
      <div className="breadcrumb">
        <p
          className="lite-text m-0 p-0"
          onClick={() => history.push("/performance")}
        >
          Performance
        </p>
        &nbsp;
        <RightOutlined className="me-2 mt-1" />
        <p
          className="lite-text m-0 p-0"
          onClick={() => {
            setIsAddEditTemplate(false);
            setStatus("");
          }}
        >
          Template
        </p>
        &nbsp;
        <RightOutlined className="mt-1" />
        &nbsp;
        <p className="m-0 p-0">
          {status === "edit"
            ? "Editing"
            : status === "add" || status === "draft"
            ? "Adding Template"
            : "view"}
        </p>
      </div>

      {(status === "add" || status === "draft") && (
        <StepsProgress stepdata={stepdata} title="Add Location:" step={step} />
      )}

      {(status === "edit" || status === "view") && (
        <Tabs defaultActiveKey={tabId} items={items} onChange={onChange} />
      )}

      {(((status === "add" || status === "draft") && step === 0) ||
        ((status === "edit" || status === "view") && tabId === "1")) && (
        <BasicAndKPI
          step={step}
          setStep={setStep}
          tableData={tableData}
          setTableData={setTableData}
          data={data}
          setData={setData}
          setIsAddEditTemplate={setIsAddEditTemplate}
          status={status}
          setStatus={setStatus}
          onSubmitClick={onSubmitClick}
          draftLoading={draftLoading}
          loading={loading}
          scrollToTop={scrollToTop}
          validateAll={validateAll}
        />
      )}

      {(((status === "add" || status === "draft") && step === 1) ||
        ((status === "edit" || status === "view") && tabId === "2")) && (
        <AppraiseeForm
          step={step}
          setStep={setStep}
          introductoryDetail={introductoryDetail}
          setIntroductoryDetail={setIntroductoryDetail}
          mainSectionDetail={mainSectionDetail}
          setMainSectionDetail={setMainSectionDetail}
          remarkSectionDetail={remarkSectionDetail}
          setRemarkSectionDetail={setRemarkSectionDetail}
          saveAppraiseDetails={saveAppraiseDetails}
          setIsAddEditTemplate={setIsAddEditTemplate}
          status={status}
          setStatus={setStatus}
          onSubmitClick={onSubmitClick}
          draftLoading={draftLoading}
          loading={loading}
          scrollToTop={scrollToTop}
          validateAll={validateAll}
          setAppraisarIntroductoryDetail={setAppraisarIntroductoryDetail}
          setAppraisarMainSectionDetail={setAppraisarMainSectionDetail}
          setAppraisarRemarkSectionDetail={setAppraisarRemarkSectionDetail}
        />
      )}

      {(((status === "add" || status === "draft") && step === 2) ||
        ((status === "edit" || status === "view") && tabId === "3")) && (
        <AppraiserForm
          step={step}
          setStep={setStep}
          appraisarIntroductoryDetail={appraisarIntroductoryDetail}
          setAppraisarIntroductoryDetail={setAppraisarIntroductoryDetail}
          appraisarMainSectionDetail={appraisarMainSectionDetail}
          setAppraisarMainSectionDetail={setAppraisarMainSectionDetail}
          appraisarRemarkSectionDetail={appraisarRemarkSectionDetail}
          setAppraisarRemarkSectionDetail={setAppraisarRemarkSectionDetail}
          setIsAddEditTemplate={setIsAddEditTemplate}
          status={status}
          setStatus={setStatus}
          onSubmitClick={onSubmitClick}
          draftLoading={draftLoading}
          loading={loading}
          scrollToTop={scrollToTop}
          mainSectionDetail={mainSectionDetail}
          validateAll={validateAll}
        />
      )}

      {(((status === "add" || status === "draft") && step === 3) ||
        ((status === "edit" || status === "view") && tabId === "4")) && (
        <ScoreAndIncrement
          step={step}
          setStep={setStep}
          data={scoreData}
          setData={setScoreData}
          tableData={salaryData}
          setTableData={setSalaryData}
          setIsAddEditTemplate={setIsAddEditTemplate}
          status={status}
          setStatus={setStatus}
          onSubmitClick={onSubmitClick}
          loading={loading}
          draftLoading={draftLoading}
          scrollToTop={scrollToTop}
          validateAll={validateAll}
        />
      )}

      <SuccessDeclinePopup
        open={popupOpen}
        setOpen={setPopupOpen}
        type="success"
        message={
          updateStatus === "draft"
            ? "Drafted"
            : status === "edit"
            ? "Updated"
            : "Completed"
        }
      />
    </Wrap>
  );
};

export default AddEditTemplate;
